import React, { useState, useEffect } from 'react';
import './InactivityScreen.css';

// ========== CONFIGURATION ==========

const CAROUSEL_INTERVAL = 30000; // 7 seconds between images

// Enable/disable page flip effect (set to false for simple fade transition)
const ENABLE_PAGE_FLIP = false;

// ====================================

const InactivityScreen = () => {
  const [carouselImages, setCarouselImages] = useState([]);
  const [isLoadingImages, setIsLoadingImages] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [previousIndex, setPreviousIndex] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [ripples, setRipples] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Load images dynamically from API endpoint
  useEffect(() => {
    fetch('/img/screensaver/images.json')
      .then(response => {
        if (!response.ok) throw new Error('Failed to load images');
        return response.json();
      })
      .then(images => {
        setCarouselImages(images);
        if (images.length === 0) {
          console.warn('No screensaver images found - screensaver disabled');
        }
        setIsLoadingImages(false);
      })
      .catch(error => {
        console.error('Error loading screensaver images:', error);
        setCarouselImages([]);
        setIsLoadingImages(false);
      });
  }, []);

  // Initial fade-in effect for first image - only after images are loaded
  useEffect(() => {
    // Wait for images to load first
    if (isLoadingImages) return;

    // Start with first image faded out, then fade in
    const timer = setTimeout(() => {
      setIsInitialLoad(false);
    }, 100); // Small delay to ensure CSS transition triggers

    return () => clearTimeout(timer);
  }, [isLoadingImages]); // Trigger only after images are loaded

  // Fullscreen slider rotation
  useEffect(() => {
    // Don't start slider until images are loaded
    if (isLoadingImages || carouselImages.length === 0) return;

    const timer = setInterval(() => {
      if (ENABLE_PAGE_FLIP) {
        // Page flip effect with transition
        setIsTransitioning(true);

        // Update indices using functional updates to avoid stale closures
        setCurrentIndex(current => {
          setPreviousIndex(current); // Save current as previous
          return (current + 1) % carouselImages.length; // Move to next
        });

        // Clean up transition state after animation completes
        setTimeout(() => {
          setIsTransitioning(false);
          setPreviousIndex(null);
        }, 1300); // Slightly longer than CSS transition (1200ms)
      } else {
        // Simple fade transition
        setCurrentIndex(current => (current + 1) % carouselImages.length);
      }
    }, CAROUSEL_INTERVAL);

    return () => clearInterval(timer);
  }, [isLoadingImages, carouselImages]); // Depend on images being loaded

  // Detect if image fails to load
  const handleImageError = (imagePath) => {
    const imageName = imagePath.split('/').pop();
    console.error(`Failed to load image: ${imageName}`);
    console.error(`Full path: ${imagePath}`);
  };

  // Add ripple effect
  const addRipple = (x, y) => {
    const id = Date.now();
    setRipples(prev => [...prev, { id, x, y }]);
    setTimeout(() => {
      setRipples(prev => prev.filter(r => r.id !== id));
    }, 600);
  };

  // Handle click/tap
  const handleClick = (e) => {
    addRipple(e.clientX, e.clientY);
    setTimeout(() => {
      window.location.href = '/guru/login';
    }, 120);
  };

  // Handle keyboard (accessibility)
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      addRipple(window.innerWidth / 2, window.innerHeight / 2);
      setTimeout(() => {
        window.location.href = '/guru/login';
      }, 120);
    }
  };


  // Don't render anything until images are loaded (prevents black screen flash)
  if (isLoadingImages) {
    return null;
  }

  // Don't show screensaver if no images available (keep current page visible)
  if (carouselImages.length === 0) {
    return null;
  }

  return (
    <div className={`inactivity-stage ${!isInitialLoad ? 'loaded' : ''}`}>
      {/* Fullscreen background slides */}
      {carouselImages.map((img, index) => {
        const isActive = index === currentIndex;
        const isExiting = ENABLE_PAGE_FLIP && isTransitioning && index === previousIndex;

        // Build class name based on whether page flip is enabled
        let className = 'inactivity-slide';
        if (ENABLE_PAGE_FLIP) {
          const isVisible = isActive || isExiting;
          className += isActive ? ' active' : '';
          className += isExiting ? ' exiting' : '';

          return (
            <div
              key={index}
              className={className}
              style={{
                backgroundImage: `url(${img})`,
                display: isVisible ? 'block' : 'none',
              }}
              aria-hidden="true"
            />
          );
        } else {
          // Simple fade mode - all slides are always rendered, only opacity changes
          const shouldBeVisible = isActive && !isInitialLoad;
          className += shouldBeVisible ? ' active' : '';

          return (
            <div
              key={index}
              className={className}
              style={{
                backgroundImage: `url(${img})`,
              }}
              aria-hidden="true"
            >
              {/* Hidden img tag to detect loading errors */}
              <img
                src={img}
                alt=""
                style={{ display: 'none' }}
                onError={() => handleImageError(img)}
              />
            </div>
          );
        }
      })}

      {/* Optional vignette overlay for better text contrast */}
      <div className="inactivity-vignette" aria-hidden="true" />

      {/* CTA - uncomment if needed */}
      {/* <div className="inactivity-cta" aria-hidden="true">
        <p className="inactivity-brand">GURU</p>
        <p className="inactivity-tap">Tap anywhere to begin</p>
      </div> */}

      {/* Click/tap capture overlay */}
      <button
        className="inactivity-overlay"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        aria-label="Tap anywhere to begin"
      />

      {/* Ripple effects */}
      {ripples.map(ripple => (
        <div
          key={ripple.id}
          className="inactivity-ripple"
          style={{ left: ripple.x, top: ripple.y }}
        />
      ))}
    </div>
  );
};

export default InactivityScreen;
